import { useEffect, useMemo, useState } from 'react'
import { CalendarRange, Check, ChevronLeft, Mountain, Plus, Route as RouteIcon, Trash2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import RouteBuilder from './RouteBuilder'
import { leaveCost, describeLeaveCost, parseLeaveDates } from '../lib/leaveCost'

const EMPTY_TRIP = {
  name: '',
  slug: '',
  color: '#f97316',
  status: 'planned',
  start_date: '',
  end_date: '',
  leave_days: '',
  countries_count: '1',
  summary: '',
  leave_dates: '',
}

function cleanOptional(value) {
  return value === '' || value === null || value === undefined ? null : value
}

function tripToForm(trip) {
  return {
    name: trip.name || '',
    slug: trip.slug || '',
    color: trip.color || '#f97316',
    status: trip.status || 'planned',
    start_date: trip.start_date || '',
    end_date: trip.end_date || '',
    leave_days: trip.leave_days ?? '',
    countries_count: trip.countries_count ?? '1',
    summary: trip.summary || '',
    leave_dates: Array.isArray(trip.leave_dates) ? trip.leave_dates.join(', ') : '',
  }
}

// One screen per expedition: the trip itself, the peaks on it, and the
// itinerary — instead of creating a trip on one tab, its summits on a second
// and its stops on a third.
export default function ExpeditionEditor({
  trips,
  summits,
  routePoints,
  routeLegs,
  routeLegsReady,
  routeLegsError,
  session,
  busy,
  setBusy,
  setNotice,
  refresh,
}) {
  const [selectedTripId, setSelectedTripId] = useState('')
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY_TRIP)

  useEffect(() => {
    if (creating) return
    if (!trips.length) {
      setSelectedTripId('')
      return
    }
    if (!trips.some((trip) => String(trip.id) === String(selectedTripId))) {
      setSelectedTripId(trips[0].id)
    }
  }, [trips, selectedTripId, creating])

  const selectedTrip = useMemo(
    () => trips.find((trip) => String(trip.id) === String(selectedTripId)) || null,
    [trips, selectedTripId],
  )

  useEffect(() => {
    if (creating) setForm(EMPTY_TRIP)
    else if (selectedTrip) setForm(tripToForm(selectedTrip))
  }, [selectedTrip, creating])

  const tripSummits = useMemo(
    () => summits.filter((summit) => String(summit.trip_id) === String(selectedTripId)),
    [summits, selectedTripId],
  )

  const tripStops = useMemo(
    () => routePoints.filter((point) => String(point.trip_id) === String(selectedTripId)),
    [routePoints, selectedTripId],
  )

  const cost = useMemo(() => leaveCost(form.leave_dates), [form.leave_dates])

  function buildPayload() {
    const dates = parseLeaveDates(form.leave_dates)
    return {
      name: form.name.trim(),
      slug: form.slug.trim() || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      color: form.color,
      status: form.status,
      start_date: cleanOptional(form.start_date),
      end_date: cleanOptional(form.end_date),
      leave_days: cleanOptional(form.leave_days) === null ? null : Number(form.leave_days),
      countries_count: Number(form.countries_count || 1),
      summary: cleanOptional(form.summary),
      // date[] column — an empty array rather than a string, or Postgres rejects it.
      leave_dates: dates.length ? dates : null,
    }
  }

  async function saveTrip(event) {
    event.preventDefault()
    if (!supabase || busy) return
    setBusy(true)
    const payload = buildPayload()

    if (creating) {
      const { data, error } = await supabase.from('trips').insert(payload).select('id').single()
      setBusy(false)
      setNotice(error ? error.message : `${payload.name} created.`)
      if (!error) {
        setCreating(false)
        if (data?.id) setSelectedTripId(data.id)
        await refresh()
      }
      return
    }

    const { error } = await supabase.from('trips').update(payload).eq('id', selectedTripId)
    setBusy(false)
    setNotice(error ? error.message : `${payload.name} saved.`)
    if (!error) await refresh()
  }

  async function deleteTrip() {
    if (!supabase || !selectedTrip) return
    const message = `Delete ${selectedTrip.name}? Its ${tripStops.length} stop${tripStops.length === 1 ? '' : 's'} go too. Summits stay, but lose their trip.`
    if (!window.confirm(message)) return
    setBusy(true)
    const { error } = await supabase.from('trips').delete().eq('id', selectedTrip.id)
    setBusy(false)
    setNotice(error ? error.message : `${selectedTrip.name} deleted.`)
    if (!error) {
      setSelectedTripId('')
      await refresh()
    }
  }

  async function updateSummit(summit, changes, message) {
    if (!supabase || busy) return
    setBusy(true)
    const { error } = await supabase.from('summits').update(changes).eq('id', summit.id)
    setBusy(false)
    setNotice(error ? error.message : message)
    if (!error) await refresh()
  }

  async function detachSummit(summit) {
    if (!window.confirm(`Remove ${summit.peak_name} from ${selectedTrip?.name}? The summit itself is kept.`)) return
    await updateSummit(summit, { trip_id: null }, `${summit.peak_name} removed from the trip.`)
  }

  return (
    <div className="expedition-editor">
      <div className="expedition-strip" role="tablist" aria-label="Expeditions">
        {trips.map((trip) => (
          <button
            type="button"
            key={trip.id}
            role="tab"
            aria-selected={!creating && String(trip.id) === String(selectedTripId)}
            className={!creating && String(trip.id) === String(selectedTripId) ? 'active' : ''}
            onClick={() => { setCreating(false); setSelectedTripId(trip.id) }}
          >
            <i style={{ background: trip.color }} />
            <span>
              <strong>{trip.name}</strong>
              <small>{summits.filter((summit) => String(summit.trip_id) === String(trip.id)).length} peaks · {trip.status}</small>
            </span>
          </button>
        ))}
        <button
          type="button"
          className={`expedition-strip__new ${creating ? 'active' : ''}`}
          onClick={() => setCreating(true)}
        >
          <Plus size={15} /> New expedition
        </button>
      </div>

      {!trips.length && !creating && (
        <div className="route-empty">No expeditions yet. Create one to start adding peaks and stops.</div>
      )}

      {(creating || selectedTrip) && (
        <>
          <form className="expedition-header" onSubmit={saveTrip}>
            {creating && (
              <button type="button" className="route-mini-button expedition-back" onClick={() => setCreating(false)}>
                <ChevronLeft size={14} /> Back
              </button>
            )}
            <div className="form-row">
              <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label>
              <label>Colour<input type="color" value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} /></label>
            </div>
            <div className="form-row">
              <label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                <option value="active">Active</option>
                <option value="planned">Planned</option>
                <option value="future">Future</option>
                <option value="completed">Completed</option>
              </select></label>
              <label>Countries<input type="number" min="1" value={form.countries_count} onChange={(event) => setForm({ ...form, countries_count: event.target.value })} /></label>
            </div>
            <div className="form-row">
              <label>Start<input type="date" value={form.start_date} onChange={(event) => setForm({ ...form, start_date: event.target.value })} /></label>
              <label>End<input type="date" value={form.end_date} onChange={(event) => setForm({ ...form, end_date: event.target.value })} /></label>
            </div>
            <div className="form-row">
              <label>Leave days<input type="number" min="0" value={form.leave_days} onChange={(event) => setForm({ ...form, leave_days: event.target.value })} /></label>
              <label>Slug<input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} placeholder="auto" /></label>
            </div>
            <label>Leave dates
              <input
                value={form.leave_dates}
                onChange={(event) => setForm({ ...form, leave_dates: event.target.value })}
                placeholder="2026-07-28, 2026-07-29, 2026-07-30"
              />
            </label>

            {cost.leaveDays > 0 && (
              <div className={`leave-cost ${cost.wasted || cost.warning ? 'leave-cost--warn' : ''}`}>
                <strong>{describeLeaveCost(cost)}</strong>
                {cost.warning || 'Clustered into as few weeks as possible — that is the efficient shape.'}
              </div>
            )}

            <label>Summary<textarea value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} /></label>

            <div className="expedition-header__actions">
              <button className="primary-button" disabled={busy}>
                <Check size={15} /> {creating ? 'Create expedition' : 'Save changes'}
              </button>
              {!creating && selectedTrip && (
                <button type="button" className="icon-button icon-button--danger" onClick={deleteTrip} disabled={busy} aria-label="Delete expedition">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </form>

          {!creating && selectedTrip && (
            <>
              <section className="expedition-peaks">
                <div className="form-title expedition-section-title">
                  <span><Mountain size={16} /> Peaks on this expedition</span>
                  <small>{tripSummits.length}</small>
                </div>
                {!tripSummits.length && (
                  <p className="route-helper">
                    None yet. Search a peak in the itinerary below and tick “Also track as a summit target” — that creates the summit and the stop in one go.
                  </p>
                )}
                {tripSummits.map((summit) => (
                  <div className="expedition-peak-row" key={summit.id}>
                    <span className="expedition-peak-row__flag">{summit.flag || '🏔️'}</span>
                    <span className="expedition-peak-row__copy">
                      <strong>{summit.peak_name}</strong>
                      <small>{summit.country_name} · {Number(summit.elevation_m || 0).toLocaleString()} m</small>
                    </span>
                    <select
                      value={summit.trip_id || ''}
                      onChange={(event) => updateSummit(
                        summit,
                        { trip_id: event.target.value || null },
                        `${summit.peak_name} moved.`,
                      )}
                      disabled={busy}
                      aria-label={`Move ${summit.peak_name} to another expedition`}
                    >
                      <option value="">No trip</option>
                      {trips.map((trip) => (
                        <option key={trip.id} value={trip.id}>{trip.name}</option>
                      ))}
                    </select>
                    <select
                      value={summit.status || 'planned'}
                      onChange={(event) => updateSummit(summit, { status: event.target.value }, `${summit.peak_name} marked ${event.target.value}.`)}
                      disabled={busy}
                      aria-label={`${summit.peak_name} status`}
                    >
                      <option value="completed">Completed</option>
                      <option value="planned">Planned</option>
                      <option value="future">Future</option>
                    </select>
                    <input
                      type="date"
                      value={summit.summit_date || ''}
                      onChange={(event) => updateSummit(summit, { summit_date: event.target.value || null }, `${summit.peak_name} date updated.`)}
                      disabled={busy}
                      aria-label={`${summit.peak_name} date`}
                    />
                    <button
                      type="button"
                      className="route-mini-button route-mini-button--danger"
                      onClick={() => detachSummit(summit)}
                      disabled={busy}
                      aria-label={`Remove ${summit.peak_name} from this trip`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </section>

              <section className="expedition-itinerary">
                <div className="form-title expedition-section-title">
                  <span><RouteIcon size={16} /> Itinerary</span>
                  <small>{tripStops.length} stop{tripStops.length === 1 ? '' : 's'}</small>
                </div>
                <RouteBuilder
                  trips={trips}
                  lockedTripId={selectedTripId}
                  session={session}
                  summits={summits}
                  routePoints={routePoints}
                  routeLegs={routeLegs}
                  routeLegsReady={routeLegsReady}
                  routeLegsError={routeLegsError}
                  busy={busy}
                  setBusy={setBusy}
                  setNotice={setNotice}
                  refresh={refresh}
                />
              </section>

              {selectedTrip.start_date && (
                <p className="expedition-footnote">
                  <CalendarRange size={13} /> Stops and peaks both belong to this expedition. Deleting the expedition removes its stops but keeps the summits.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
